import {
  Calendar,
  DollarSign,
  File,
  LucideIcon,
  Signature,
  Undo2,
} from 'lucide-react';
import { formatInTimeZone } from 'date-fns-tz';
import { GetAllInPrice } from '../../hooks/payments/useCashDiscount';
import { getPaymentMethodPhrase } from '../payments';
import {
  convertMinsToHrsMins,
  convertSnakeToTitleCase,
  formatDollarAmount,
} from '../../utils/global/helper';
import {
  AppointmentEntry,
  AppointmentMilestone,
  AppointmentMilestoneEntry,
  CustomerFileEntry,
  PaymentEntry,
  PaymentRefundEntry,
  TimelineEntry,
  WaiverEventEntry,
} from '../../types/customers';

export interface TimelineEntryContent {
  Icon: LucideIcon;
  meta: string;
  title: string;
  body: string | null;
  badge: string | null;
}

function formatEntryDateTime(occurredAt: string, timezone: string) {
  const date = new Date(occurredAt);
  const entryYear = Number(formatInTimeZone(date, timezone, 'yyyy'));
  const currentYear = Number(formatInTimeZone(new Date(), timezone, 'yyyy'));
  const pattern =
    entryYear === currentYear ? 'MMMM d, h:mm a' : 'MMMM d, yyyy, h:mm a';
  return formatInTimeZone(date, timezone, pattern);
}

function buildMeta(occurredAt: string, timezone: string, category: string) {
  return `${formatEntryDateTime(occurredAt, timezone)} · ${category}`;
}

type EntryContentBase = TimelineEntryContent;

const MILESTONE_TITLES: Record<AppointmentMilestone, string> = {
  submitted: 'Booking request submitted',
  accepted: 'Booking request accepted',
  scheduled: 'Appointment scheduled',
  confirmed: 'Appointment confirmed',
  deposit_paid: 'Deposit paid',
  checked_out: 'Checked out',
  canceled: 'Appointment canceled',
  no_show: 'No-show',
};

function describeAppointmentMilestone(
  entry: AppointmentMilestoneEntry,
  timezone: string,
): EntryContentBase {
  const title = MILESTONE_TITLES[entry.milestone];
  if (title === undefined) {
    throw new Error(`Unrecognized appointment milestone: ${entry.milestone}`);
  }
  return {
    Icon: Calendar,
    meta: buildMeta(entry.occurredAt, timezone, 'Appointment update'),
    title,
    body: null,
    badge: null,
  };
}

function describeAppointment(
  entry: AppointmentEntry,
  timezone: string,
  getAllInPrice: GetAllInPrice,
): EntryContentBase {
  const { appointment } = entry;
  const title =
    appointment.serviceName ??
    convertSnakeToTitleCase(appointment.serviceCategory);

  const bodyParts = [
    appointment.employeeName ? `with ${appointment.employeeName}` : null,
    `for ${convertMinsToHrsMins(appointment.appointmentLengthInMinutes, true)}`,
    appointment.price !== null
      ? formatDollarAmount(getAllInPrice(appointment.price))
      : null,
  ].filter((part): part is string => part !== null);

  return {
    Icon: Calendar,
    meta: buildMeta(
      entry.occurredAt,
      timezone,
      appointment.isUpcoming ? 'Upcoming appointment' : 'Appointment',
    ),
    title,
    body: bodyParts.length > 0 ? bodyParts.join(' · ') : null,
    badge: appointment.wasNoShow ? 'No-show' : null,
  };
}

function describePayment(
  entry: PaymentEntry,
  timezone: string,
): EntryContentBase {
  const { payment } = entry;
  return {
    Icon: DollarSign,
    meta: buildMeta(entry.occurredAt, timezone, 'Payment'),
    title: `${formatDollarAmount(
      payment.amount / 100,
    )} ${getPaymentMethodPhrase(payment.method)}`,
    body: `${convertSnakeToTitleCase(payment.type)} payment`,
    badge: null,
  };
}

function describePaymentRefund(
  entry: PaymentRefundEntry,
  timezone: string,
): EntryContentBase {
  const { refund } = entry;
  return {
    Icon: Undo2,
    meta: buildMeta(entry.occurredAt, timezone, 'Refund'),
    title: `${formatDollarAmount(refund.amount / 100)} refunded`,
    body: refund.reason ? convertSnakeToTitleCase(refund.reason) : null,
    badge: null,
  };
}

const WAIVER_EVENT_TITLES: Record<
  WaiverEventEntry['waiverEvent']['eventType'],
  string
> = {
  SCHEDULED: 'Waiver scheduled',
  SENT: 'Waiver sent',
  VIEWED: 'Waiver viewed',
  REMINDER: 'Waiver reminder sent',
  COMPLETED: 'Waiver signed',
  CANCELED: 'Waiver canceled',
  REACTIVATED: 'Waiver reactivated',
  EMPLOYEE_COMPLETED: 'Waiver completed by staff',
};

function describeWaiverEvent(
  entry: WaiverEventEntry,
  timezone: string,
): EntryContentBase {
  return {
    Icon: Signature,
    meta: buildMeta(entry.occurredAt, timezone, 'Waiver'),
    title: WAIVER_EVENT_TITLES[entry.waiverEvent.eventType],
    body: null,
    badge: null,
  };
}

function describeCustomerFile(
  entry: CustomerFileEntry,
  timezone: string,
): EntryContentBase {
  return {
    Icon: File,
    meta: buildMeta(entry.occurredAt, timezone, 'File added'),
    title: entry.file.name,
    body: entry.file.mimeType,
    badge: null,
  };
}

export function describeTimelineEntry(
  entry: TimelineEntry,
  timezone: string,
  getAllInPrice: GetAllInPrice,
): TimelineEntryContent {
  const content = (() => {
    switch (entry.type) {
      case 'appointment_milestone':
        return describeAppointmentMilestone(entry, timezone);
      case 'appointment':
        return describeAppointment(entry, timezone, getAllInPrice);
      case 'payment':
        return describePayment(entry, timezone);
      case 'payment_refund':
        return describePaymentRefund(entry, timezone);
      case 'waiver_event':
        return describeWaiverEvent(entry, timezone);
      case 'customer_file':
        return describeCustomerFile(entry, timezone);
      default: {
        const exhaustiveCheck: never = entry;
        throw new Error(
          `Unhandled timeline entry type: ${JSON.stringify(exhaustiveCheck)}`,
        );
      }
    }
  })();

  // Who did it reads as part of the event line: "File added by Dana".
  return entry.actor
    ? { ...content, meta: `${content.meta} by ${entry.actor.name}` }
    : content;
}
